import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Button,
  Pagination, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography
} from '@mui/material';
import UserActionsDialog from './UserActionsDialog';

// Импорт созданных хуков и компонентов
import {
  useUsers,
  useUserFilters,
  usePagination,
  useUserActions,
  useInlineEditing,
  useNotifications
} from './hooks';
import {
  SearchBar,
  UserTableRow,
  UserActionsButtons
} from './components';
import { isActivationButtonDisabled, logAction } from './utils/permissions';
import { User, ActionDialogData } from './types';



const AllUsers: React.FC = () => {
  // Хуки для управления состоянием
  const { users, loading, error, refetchUsers } = useUsers();
  const { searchTerm, searchColumn, filteredUsers, setSearchTerm, setSearchColumn } = useUserFilters(users);
  const { currentPage, totalPages, paginatedItems: paginatedUsers, handleChangePage } = usePagination(filteredUsers, searchTerm);
  const { snackbar, showNotification, handleCloseSnackbar } = useNotifications();

  // Локальное состояние для диалогов
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openActionsDialog, setOpenActionsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionDialog, setActionDialog] = useState<ActionDialogData>({
    password: '',
    role: '',
    myPharmacy: '',
    cityId: '',
    userphone: '',
    fullName: '',
    iin: ''
  });

  const currentUserRole = localStorage.getItem('role');
  const shouldShowIIN = currentUserRole === 'admin' || currentUserRole === 'moder';

  // Хуки для действий с пользователями
  const userActions = useUserActions(showNotification, logAction, refetchUsers);

  // Хук для inline редактирования
  const inlineEditing = useInlineEditing(users, showNotification, logAction, refetchUsers);

  const handleOpenActionsDialog = useCallback((user: User) => {
    setSelectedUser(user);
    setActionDialog({
      password: user.password_hash || '',
      role: user.role || '',
      myPharmacy: user.myPharmacy || '',
      cityId: user.cityId || '',
      userphone: user.userphone || '',
      fullName: user.fullName || '',
      iin: user.iin || ''
    });
    setOpenActionsDialog(true);
  }, []);

  const handleCloseActionsDialog = useCallback(() => {
    setOpenActionsDialog(false);
    setSelectedUser(null);
    setActionDialog({
      password: '',
      role: '',
      myPharmacy: '',
      cityId: '',
      userphone: '',
      fullName: '',
      iin: ''
    });
  }, []);

  // Эти функции теперь будут переданы в UserActionsDialog как колбэки
  const handleSavePassword = useCallback(async () => {
    if (selectedUser && actionDialog.password) {
      try {
        const axios = (await import('axios')).default;
        await axios.post('https://tbl.zerdegroup.kz/api/change-password', {
          username: selectedUser.username,
          newPassword: actionDialog.password,
          role: selectedUser.role
        });
        showNotification('Пароль успешно изменен', 'success');
        logAction(`изменил пароль для пользователя "${selectedUser.username}" | Пользователи`);
        refetchUsers();
      } catch (error) {
        console.error('Ошибка смены пароля:', error);
        showNotification('Не удалось изменить пароль', 'error');
      }
    }
  }, [selectedUser, actionDialog.password, showNotification, refetchUsers]);

  const handleSaveRole = useCallback(async () => {
    if (selectedUser && actionDialog.role) {
      try {
        const axios = (await import('axios')).default;
        await axios.post('https://tbl.zerdegroup.kz/api/change-role', {
          userId: selectedUser.id,
          newRole: actionDialog.role
        });
        showNotification('Роль успешно изменена', 'success');
        logAction(`изменил роль пользователя "${selectedUser.username}" на "${actionDialog.role}" | Пользователи`);
        refetchUsers();
      } catch (error) {
        console.error('Ошибка смены роли:', error);
        showNotification('Не удалось изменить роль', 'error');
      }
    }
  }, [selectedUser, actionDialog.role, showNotification, refetchUsers]);

  const handleSaveRegion = useCallback(async () => {
    if (selectedUser && actionDialog.cityId) {
      try {
        const axios = (await import('axios')).default;
        await axios.post('https://tbl.zerdegroup.kz/api/change-region', {
          userId: selectedUser.id,
          cityId: actionDialog.cityId
        });
        showNotification('Регион успешно изменен', 'success');
        logAction(`изменил регион пользователя "${selectedUser.username}" на "${actionDialog.cityId}" | Пользователи`);
        refetchUsers();
      } catch (error) {
        console.error('Ошибка смены региона:', error);
        showNotification('Не удалось изменить регион', 'error');
      }
    }
  }, [selectedUser, actionDialog.cityId, showNotification, refetchUsers]);

  const handleSavePharmacy = useCallback(async () => {
    if (selectedUser && actionDialog.myPharmacy !== undefined) {
      try {
        const axios = (await import('axios')).default;
        await axios.post('https://tbl.zerdegroup.kz/api/change-pharmacy', {
          userId: selectedUser.id,
          myPharmacy: actionDialog.myPharmacy
        });
        showNotification('Список подчиненных аптек успешно обновлен', 'success');
        logAction(`обновил список подчиненных аптек для "${selectedUser.username}" | Пользователи`);
        refetchUsers();
      } catch (error) {
        console.error('Ошибка обновления аптек:', error);
        showNotification('Не удалось обновить список аптек', 'error');
      }
    }
  }, [selectedUser, actionDialog.myPharmacy, showNotification, refetchUsers]);

  // Обработчики для диалогов удаления
  const handleOpenDeleteDialog = useCallback((user: User) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  }, []);

  const confirmDeleteUser = useCallback(async () => {
    if (userToDelete) {
      await userActions.deleteUser(userToDelete);
      handleCloseDeleteDialog();
    }
  }, [userToDelete, userActions.deleteUser, handleCloseDeleteDialog]);

  useEffect(() => {
    document.title = 'Список пользователей | Zerde';
    document.body.style.overflow = 'hidden';
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography>Загрузка пользователей...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <SearchBar
        searchTerm={searchTerm}
        searchColumn={searchColumn}
        onSearchTermChange={setSearchTerm}
        onSearchColumnChange={setSearchColumn}
        onMassReportsLock={userActions.massReportsLock}
        onMassPOSLock={userActions.massPOSLock}
        currentUserRole={currentUserRole}
      />

      <Box sx={{
        position: 'relative',
        overflow: 'hidden',
        background: '#ffffff5e'
      }}>
        <Box sx={{
          overflowX: 'auto',
          marginRight: currentUserRole === 'guide' ? '0' : '490px',
          background: '#ffffff5e'
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                {currentUserRole !== 'guide' && <TableCell>№</TableCell>}
                <TableCell>Регион</TableCell>
                <TableCell>Роль</TableCell>
                {currentUserRole !== 'guide' && <TableCell>ID</TableCell>}
                <TableCell>Телефон</TableCell>
                <TableCell>ФИО</TableCell>
                {shouldShowIIN && <TableCell>ИИН</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <UserTableRow
                  key={user.id}
                  user={user}
                  shouldShowIIN={shouldShowIIN}
                  editingId={inlineEditing.editingId}
                  editingPharmacy={inlineEditing.editingPharmacy}
                  canEditId={inlineEditing.canEditId}
                  canEditPharmacyNum={inlineEditing.canEditPharmacyNum}
                  onStartEditingId={inlineEditing.startEditingId}
                  onStartEditingPharmacy={inlineEditing.startEditingPharmacy}
                  onSaveNewId={inlineEditing.saveNewId}
                  onSaveNewPharmacyNum={inlineEditing.saveNewPharmacyNum}
                  onIdChange={(newId) =>
                    inlineEditing.setEditingId(prev => prev ? { ...prev, newId } : null)
                  }
                  onPharmacyChange={(newPharmacyNum) =>
                    inlineEditing.setEditingPharmacy(prev => prev ? { ...prev, newPharmacyNum } : null)
                  }
                  onCancelIdEdit={() => inlineEditing.setEditingId(null)}
                  onCancelPharmacyEdit={() => inlineEditing.setEditingPharmacy(null)}
                  isGuide={currentUserRole === 'guide'}
                />
              ))}
            </TableBody>
          </Table>
        </Box>

        {currentUserRole !== 'guide' && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '490px',
            height: '100%',
            background: '#ffffff5e',
            borderLeft: '1px solid rgba(224, 224, 224, 1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '490px', background: '#fff' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={`action-${user.id}`}>
                    <UserActionsButtons
                      user={user}
                      currentUserRole={currentUserRole}
                      onOpenActionsDialog={handleOpenActionsDialog}
                      onToggleActivation={userActions.toggleActivation}
                      onTogglePOS={userActions.togglePOS}
                      onToggleReports={userActions.toggleReports}
                      onOpenDeleteDialog={handleOpenDeleteDialog}
                      isActivationButtonDisabled={(user) => isActivationButtonDisabled(user, currentUserRole)}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      <UserActionsDialog
        open={openActionsDialog}
        onClose={handleCloseActionsDialog}
        user={selectedUser}
        actionDialog={actionDialog}
        setActionDialog={setActionDialog}
        onSavePassword={handleSavePassword}
        onSaveRole={handleSaveRole}
        onSaveRegion={handleSaveRegion}
        onSavePharmacy={handleSavePharmacy}
        setShouldRefetch={refetchUsers}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 3, float: 'right' }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handleChangePage}
          color="primary"
        />
      </Box>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>Вы уверены, что хотите удалить пользователя <b>{userToDelete?.username}</b>?
            <br /> Действие нельзя отменить!</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" variant='contained'>
            Отмена
          </Button>
          <Button onClick={confirmDeleteUser} color="error" variant='contained'>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllUsers;